import type {FormControlProps, FormErrorMessageProps, FormLabelProps, TextProps} from '@chakra-ui/react';
import {FormControl, FormErrorMessage, FormHelperText, FormLabel} from '@chakra-ui/react';
import get from 'lodash/get';
import type {FieldError, FieldPath, FieldValues} from 'react-hook-form';
import {useFormContext} from 'react-hook-form';
import type {ReactNode} from 'react';

import {Label} from '../../../components/Label';

/**
 * All properties from Chakra's FormControlProps except "label" which
 * we are overriding to accept a ReactNode instead of a simple string
 */
export type ChakraFormControlProps = Omit<FormControlProps, 'label'> & {};

/**
 * Defines react-hook-form-chakra specific properties for all components
 */
export type BaseReactHookFormProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  /**
   * The name of the input (required)
   */
  name: TName;

  /**
   * The label to be associated with the input
   */
  label?: ReactNode;
  /**
   * Chakra FormLabelProps
   */
  labelProps?: FormLabelProps;

  /**
   * Helper text to show alongside input
   */
  helperText?: ReactNode;

  /**
   * Chakra TextProps associated with the helper text
   */
  helperTextProps?: TextProps;

  /**
   * Chakra FormErrorMessageProps for error message
   */
  errorMessageProps?: FormErrorMessageProps;
};

/**
 * The union of ChakraFormControlProps and BaseReactHookFormProps which defines the base properties
 * for most of react-hook-form-chakra components
 */
export type FieldControlProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ChakraFormControlProps & BaseReactHookFormProps<TFieldValues, TName> & {};

export const FieldControl = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldControlProps<TFieldValues, TName>,
) => {
  const {children, name, label, labelProps, helperText, helperTextProps, errorMessageProps, ...rest} = props;

  // Do not use getFieldState
  // as it's too memoized, and returns state one render after
  const {
    formState: {errors},
  } = useFormContext<TFieldValues>();
  const error = get(errors, name) as FieldError | undefined;

  return (
    <FormControl isInvalid={!!error} {...rest}>
      {label && typeof label === `string` ? (
        <FormLabel htmlFor={`${name}`} {...labelProps} as={Label} fontSize={`14px`} fontWeight={500} mb={`space-050`}>
          {label}
        </FormLabel>
      ) : (
        label
      )}
      {children}
      {error?.message && <FormErrorMessage {...errorMessageProps}>{error.message}</FormErrorMessage>}
      {helperText && typeof helperText === `string` ? (
        <FormHelperText {...helperTextProps}>{helperText}</FormHelperText>
      ) : (
        helperText
      )}
    </FormControl>
  );
};
